import { useMemo } from 'react'

import i18n from 'modules/core/lib/i18n'
import { EmptyHandlerForm } from 'modules/workflows/components/Sidepanel/handlerForms/EmptyHandlerForm'
import { createApprovalFormSchema } from 'modules/workflows/components/Sidepanel/schemas/approvalFormSchema'
import type { HandlersConfig } from 'modules/workflows/contexts/WorkflowContext'
import { mapWorkflowNodeFilesToNodes } from 'modules/workflows/lib/mapWorkflowNodeFilesToNodes'

import {
  BasicTaskHandlerForm,
  DownloadDocumentHandlerForm,
  RequestDocumentHandlerForm,
  RequestReviewHandlerForm,
} from './sidePanels/handlers'
import { createBasicTaskHandlerSchema } from './sidePanels/schemas/basicTaskHandler'
import {
  type DownloadDocumentHandlerSchema,
  createDownloadDocumentHandlerSchema,
} from './sidePanels/schemas/downloadDocumentHandler'
import {
  type ManualApproveHandlerSchema,
  createManualApproveHandlerSchema,
} from './sidePanels/schemas/manualApproveHandler'
import { basicTaskApproveNodeToSchemaMapper } from './sidePanels/schemas/nodeToSchemaMappers/basicTask'
import { downloadDocumentNodeToSchemaMapper } from './sidePanels/schemas/nodeToSchemaMappers/downloadDocument'
import { manualApproveNodeToSchemaMapper } from './sidePanels/schemas/nodeToSchemaMappers/manualApprove'
import { requestDocumentNodeToSchemaMapper } from './sidePanels/schemas/nodeToSchemaMappers/requestDocument'
import {
  type RequestDocumentHandlerSchema,
  createRequestDocumentHandlerSchema,
} from './sidePanels/schemas/requestDocumentHandler'

function assertPayloadType<T>(
  payload: unknown,
  handlerType: string,
  expected: string
): asserts payload is T {
  if (handlerType !== expected) throw new Error(`Invalid payload for ${expected} handler`)
}

export function useProcurementHandlers(): HandlersConfig {
  return useMemo(
    () => ({
      auto_approve: {
        dropDownLabel: i18n.t('workflows.approval.types.auto_approve'),
        component: EmptyHandlerForm,
        initializer: () => ({}),
        schema: createApprovalFormSchema,
      },
      auto_reject: {
        dropDownLabel: i18n.t('workflows.approval.types.auto_reject'),
        component: EmptyHandlerForm,
        initializer: () => ({}),
        schema: createApprovalFormSchema,
      },
      request_review: {
        dropDownLabel: i18n.t('workflows.approval.types.request_review'),
        component: RequestReviewHandlerForm,
        beforeSave: (payload, handlerType) => {
          assertPayloadType<ManualApproveHandlerSchema['nodeData']['payload']>(
            payload,
            handlerType,
            'request_review'
          )

          return {
            ...payload,
            employee_segments: payload.employee_segments || [],
          }
        },
        schema: createManualApproveHandlerSchema,
        initializer: manualApproveNodeToSchemaMapper,
      },
      create_manual_task: {
        dropDownLabel: i18n.t('workflows.task.types.basic'),
        component: BasicTaskHandlerForm,
        initializer: basicTaskApproveNodeToSchemaMapper,
        schema: createBasicTaskHandlerSchema,
      },
      request_document: {
        dropDownLabel: i18n.t('workflows.task.types.request_document'),
        component: RequestDocumentHandlerForm,
        initializer: requestDocumentNodeToSchemaMapper,
        schema: createRequestDocumentHandlerSchema,
        beforeSave: (payload, handlerType) => {
          assertPayloadType<RequestDocumentHandlerSchema['nodeData']['payload']>(
            payload,
            handlerType,
            'request_document'
          )

          const filteredEmployeeIds = (payload.employee_ids || []).filter(
            (id): id is string => typeof id === 'string'
          )

          return {
            ...payload,
            employee_ids: filteredEmployeeIds,
          }
        },
      },
      download_document: {
        dropDownLabel: i18n.t('workflows.task.types.download_document'),
        component: DownloadDocumentHandlerForm,
        initializer: downloadDocumentNodeToSchemaMapper,
        schema: createDownloadDocumentHandlerSchema,
        beforeSave: (payload, handlerType, context) => {
          assertPayloadType<DownloadDocumentHandlerSchema['nodeData']['payload']>(
            payload,
            handlerType,
            'download_document'
          )

          if (!context) {
            throw new Error('Context is required for download_document handler')
          }

          const filteredEmployeeIds = (payload.employee_ids || []).filter(
            (id): id is string => typeof id === 'string'
          )

          const workflowNodeFiles = context.workflow.workflowNodeFiles || []
          const filesByNode = mapWorkflowNodeFilesToNodes(
            workflowNodeFiles,
            context.workflow.workflowNodes
          )
          const nodeFiles = filesByNode.get(context.nodeId) ?? []
          const workflowNodeFilesIds = nodeFiles.map((file) => file.id)

          return {
            ...payload,
            employee_ids: filteredEmployeeIds,
            workflow_node_files_ids: workflowNodeFilesIds,
            files: undefined,
          }
        },
      },
    }),
    []
  )
}
